import { useRef, useState } from "react";
import useDialogFocus from "@/lib/useDialogFocus";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useStore } from "@/context/StoreContext";
import { api, imgSrc, money, errMsg } from "@/lib/api";

function BagLine({ it }) {
  const { setQty, remove } = useCart();
  const opts = Object.values(it.options || {}).filter(Boolean).join(" · ");
  return (
    <li data-testid={`bag-item-${it.key}`} className="flex gap-4 border-b border-hairline py-5">
      <img src={imgSrc(it.image)} alt={it.name} className="h-24 w-[4.5rem] shrink-0 bg-[#333336] object-cover object-top" />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-3">
          <p className="font-display text-lg font-extralight leading-tight text-ink">{it.name}</p>
          <p className="font-body text-sm text-ink">{money(it.price * it.qty)}</p>
        </div>
        {opts && <p className="mt-1 font-body text-xs text-muted">{opts}</p>}
        {it.details && <p className="mt-1 line-clamp-2 font-body text-xs text-muted">{it.details}</p>}
        <div className="mt-auto flex items-center justify-between pt-3">
          <div className="flex items-center gap-3 border border-ink/20 px-2 py-1">
            <button type="button" data-testid={`bag-qty-minus-${it.key}`} onClick={() => setQty(it.key, it.qty - 1)} aria-label={`Decrease quantity of ${it.name}`}><Minus className="h-3 w-3" /></button>
            <span data-testid={`bag-qty-${it.key}`} aria-live="polite" aria-label={`Quantity ${it.qty}`} className="w-4 text-center font-mono text-xs">{it.qty}</span>
            <button type="button" data-testid={`bag-qty-plus-${it.key}`} onClick={() => setQty(it.key, it.qty + 1)} aria-label={`Increase quantity of ${it.name}`}><Plus className="h-3 w-3" /></button>
          </div>
          <button type="button" data-testid={`bag-remove-${it.key}`} onClick={() => remove(it.key)} className="overline text-[0.58rem] text-muted hover:text-ink">Remove</button>
        </div>
      </div>
    </li>
  );
}

const inputCls = "w-full border-b border-ink/20 bg-transparent pb-2 font-body text-sm text-ink outline-none placeholder:text-muted/80 focus:border-ink";

function GiftNote({ gift, setGift }) {
  return (
    <div data-testid="bag-gift" className="mt-4">
      <label className="flex cursor-pointer items-center gap-2.5 font-body text-sm text-ink">
        <input type="checkbox" data-testid="bag-gift-toggle" checked={gift.is_gift} onChange={(e) => setGift({ ...gift, is_gift: e.target.checked })} className="h-4 w-4 accent-[#2C2A28]" />
        This is a gift — add a note
      </label>
      {gift.is_gift && (
        <div className="mt-3 space-y-3">
          <input aria-label="Who is the gift for?" data-testid="bag-gift-recipient" value={gift.recipient} maxLength={120} onChange={(e) => setGift({ ...gift, recipient: e.target.value })} placeholder="Who is it for?" className={inputCls} />
          <textarea aria-label="Gift message" data-testid="bag-gift-message" rows={2} value={gift.message} maxLength={500} onChange={(e) => setGift({ ...gift, message: e.target.value })} placeholder="Your gift message — handwritten by Phaedra and tucked in" className={`${inputCls} resize-none`} />
        </div>
      )}
    </div>
  );
}

function BagFooter() {
  const { items, subtotal } = useCart();
  const { settings } = useStore();
  const [note, setNote] = useState("");
  const [gift, setGift] = useState({ is_gift: false, recipient: "", message: "" });
  const [busy, setBusy] = useState(false);
  const freeOver = settings?.free_shipping_over;
  const shipping = settings ? (freeOver != null && subtotal >= freeOver ? 0 : settings.shipping_flat) : 0;

  const checkout = async () => {
    setBusy(true);
    try {
      const details = items.filter((i) => i.details).map((i) => `${i.name}: ${i.details}`);
      const { data } = await api.post("/store/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty, options: i.options })),
        note: [...details, note].filter(Boolean).join("\n"),
        gift,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div className="border-t border-hairline px-6 py-6">
      <textarea aria-label="A note for Phaedra (optional)" data-testid="bag-note" value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={800}
        placeholder="A note for Phaedra (optional)"
        className="w-full resize-none border-b border-ink/20 bg-transparent pb-2 font-body text-sm text-ink outline-none placeholder:text-muted/80 focus:border-ink" />
      <GiftNote gift={gift} setGift={setGift} />
      <div className="mt-5 space-y-1.5 font-body text-sm text-stone">
        <div className="flex justify-between"><span>Subtotal</span><span data-testid="bag-subtotal">{money(subtotal)}</span></div>
        <div className="flex justify-between"><span>Shipping</span><span data-testid="bag-shipping">{shipping === 0 ? "Free" : money(shipping)}</span></div>
        {freeOver != null && subtotal < freeOver && (
          <p className="text-xs text-muted">Free shipping on orders over {money(freeOver)}.</p>
        )}
      </div>
      <button type="button" data-testid="bag-checkout-btn" onClick={checkout} disabled={busy}
        className="group mt-6 flex w-full items-center justify-center gap-3 bg-ink px-8 py-4 text-paper transition-colors duration-500 hover:bg-ink/85 disabled:opacity-50">
        <span className="overline">{busy ? "Opening secure checkout…" : "Checkout"}</span>
        <span className="transition-transform duration-500 group-hover:translate-x-1.5">↗</span>
      </button>
      <p className="mt-3 text-center font-body text-[0.7rem] text-muted">Secure payment by Stripe. Tax calculated at checkout where applicable.</p>
    </div>
  );
}

export default function CartDrawer() {
  const { items, open, setOpen } = useCart();
  const panel = useRef(null);
  useDialogFocus(open, panel, () => setOpen(false));
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div key="bg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setOpen(false)} aria-hidden="true" className="fixed inset-0 z-[70] bg-ink/30 backdrop-blur-[2px]" />
          <motion.aside key="panel" ref={panel} role="dialog" aria-modal="true" aria-labelledby="bag-title" data-testid="bag-drawer" data-lenis-prevent
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 z-[71] flex h-full w-full max-w-md flex-col bg-[#F2F1EE]">
            <div className="flex items-center justify-between border-b border-hairline px-6 py-5">
              <h2 id="bag-title" className="overline text-ink">Your bag</h2>
              <button type="button" data-testid="bag-close-btn" onClick={() => setOpen(false)} aria-label="Close bag"><X className="h-5 w-5 text-ink" strokeWidth={1.4} /></button>
            </div>
            {items.length === 0 ? (
              <p data-testid="bag-empty" className="px-6 py-16 font-display text-2xl font-extralight text-stone">Your bag is empty.</p>
            ) : (
              <>
                <ul className="flex-1 overflow-y-auto px-6">{items.map((it) => <BagLine key={it.key} it={it} />)}</ul>
                <BagFooter />
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
